// src/services/orderAttachmentsService.js
/**
 * Order Attachments Service
 * Handles file attachments for Purchase Orders (Órdenes de Compra)
 * 
 * API Endpoints:
 * - POST   /orders/:orderId/attachments                    - Upload file
 * - GET    /orders/:orderId/attachments                    - List attachments
 * - GET    /orders/:orderId/attachments/:id/download       - Download file
 * - DELETE /orders/:orderId/attachments/:id                - Delete file
 * 
 * Constraints:
 * - Max file size: 10 MB
 * - Max files per order: 5
 * - Allowed types: jpg, jpeg, png, pdf
 */

import axios from 'axios';
import { NODE_SUPPLIERS_API_URL } from '../config';

const ORDERS_API_URL = `${NODE_SUPPLIERS_API_URL}/orders`;

// Constants for validation
export const ATTACHMENT_CONSTRAINTS = {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB in bytes
    MAX_FILES_PER_ORDER: 5,
    ALLOWED_EXTENSIONS: ['jpg', 'jpeg', 'png', 'pdf'],
    ALLOWED_MIME_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'],
};

/**
 * Upload a file attachment to an order
 * @param {number} orderId - The order ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (receives percentage 0-100)
 * @returns {Promise<Object>} The created attachment object
 */
export const uploadAttachment = async (orderId, file, onProgress = null) => {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const config = {
            headers: { 
                'Content-Type': 'multipart/form-data' 
            }
        };

        if (onProgress && typeof onProgress === 'function') {
            config.onUploadProgress = (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percent);
            };
        }

        const response = await axios.post(
            `${ORDERS_API_URL}/${orderId}/attachments`,
            formData,
            config
        );

        return response.data;
    } catch (error) {
        console.error('Error uploading attachment:', error);
        throw error;
    }
};

/**
 * Get all attachments for an order
 * @param {number} orderId - The order ID
 * @returns {Promise<Object>} Object with attachments array, count, and max_allowed
 */
export const getAttachments = async (orderId) => {
    try {
        const response = await axios.get(`${ORDERS_API_URL}/${orderId}/attachments`);
        return response.data;
    } catch (error) {
        console.error('Error fetching attachments:', error);
        throw error;
    }
};

/**
 * Get the download URL for an attachment (note: requires auth header)
 * @param {number} orderId - The order ID
 * @param {number} attachmentId - The attachment ID
 * @returns {string} The full download URL
 */
export const getDownloadUrl = (orderId, attachmentId) => {
    return `${ORDERS_API_URL}/${orderId}/attachments/${attachmentId}/download`;
};

/**
 * View an attachment file in a new tab (fetches with auth, then opens blob URL)
 * @param {number} orderId - The order ID
 * @param {number} attachmentId - The attachment ID
 * @param {string} mimeType - The file's MIME type
 * @returns {Promise<void>}
 */
export const viewAttachment = async (orderId, attachmentId, mimeType) => {
    try {
        const response = await axios.get(
            `${ORDERS_API_URL}/${orderId}/attachments/${attachmentId}/download`,
            { responseType: 'blob' }
        );

        // Create blob with correct MIME type
        const blob = new Blob([response.data], { type: mimeType || 'application/octet-stream' });
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Open in new tab
        window.open(blobUrl, '_blank');
        
        // Revoke after a delay to allow the new tab to load
        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
        }, 10000);
    } catch (error) {
        console.error('Error viewing attachment:', error);
        throw error;
    }
};

/**
 * Download an attachment file using fetch (for programmatic downloads)
 * @param {number} orderId - The order ID
 * @param {number} attachmentId - The attachment ID
 * @param {string} filename - The filename to save as
 */
export const downloadAttachment = async (orderId, attachmentId, filename) => {
    try {
        const response = await axios.get(
            `${ORDERS_API_URL}/${orderId}/attachments/${attachmentId}/download`,
            { responseType: 'blob' }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading attachment:', error);
        throw error;
    }
};

/**
 * Delete an attachment from an order
 * @param {number} orderId - The order ID
 * @param {number} attachmentId - The attachment ID
 * @returns {Promise<Object>} Response with deleted_id
 */
export const deleteAttachment = async (orderId, attachmentId) => {
    try {
        const response = await axios.delete(
            `${ORDERS_API_URL}/${orderId}/attachments/${attachmentId}`
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting attachment:', error);
        throw error;
    }
};

/**
 * Validate a file before upload (client-side validation)
 * @param {File} file - The file to validate
 * @param {number} currentCount - Current number of attachments on the order
 * @returns {Object} { valid: boolean, error?: string }
 */
export const validateFile = (file, currentCount = 0) => {
    const { MAX_FILE_SIZE, MAX_FILES_PER_ORDER, ALLOWED_EXTENSIONS, ALLOWED_MIME_TYPES } = ATTACHMENT_CONSTRAINTS;

    // Check if file exists
    if (!file) {
        return { valid: false, error: 'No se seleccionó ningún archivo' };
    }

    // Check file count limit
    if (currentCount >= MAX_FILES_PER_ORDER) {
        return { 
            valid: false, 
            error: `Máximo ${MAX_FILES_PER_ORDER} archivos por orden. Elimine uno primero.` 
        };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
        const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
        return { 
            valid: false, 
            error: `El archivo excede el tamaño máximo de ${maxSizeMB}MB` 
        };
    }

    // Check file extension
    const extension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
        return { 
            valid: false, 
            error: `Tipo de archivo no permitido. Solo: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}` 
        };
    }

    // Check MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return { 
            valid: false, 
            error: 'Tipo MIME no válido' 
        };
    }

    return { valid: true };
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Human-readable size string
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file type icon identifier based on file type
 * @param {string} fileType - File extension (jpg, jpeg, png, pdf)
 * @returns {string} Icon type: 'image' or 'pdf'
 */
export const getFileIconType = (fileType) => {
    const imageTypes = ['jpg', 'jpeg', 'png'];
    return imageTypes.includes(fileType?.toLowerCase()) ? 'image' : 'pdf';
};

// Default export with all functions
const orderAttachmentsService = {
    uploadAttachment,
    getAttachments,
    getDownloadUrl,
    viewAttachment,
    downloadAttachment,
    deleteAttachment,
    validateFile,
    formatFileSize,
    getFileIconType,
    ATTACHMENT_CONSTRAINTS,
};

export default orderAttachmentsService;
